import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';

interface PreloaderProps {
  onComplete: () => void;
  key?: string | number;
}

export default function Preloader({ onComplete }: PreloaderProps) {
  const [progress, setProgress] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const [logoImgError, setLogoImgError] = useState(false);

  useEffect(() => {
    // Elegant incremental loading speed simulation
    let currentProgress = 0;
    const interval = setInterval(() => {
      // Dynamic intervals for realistic loading motion
      const increment = Math.floor(Math.random() * 8) + 4;
      currentProgress = Math.min(currentProgress + increment, 100);
      setProgress(currentProgress);

      if (currentProgress >= 100) {
        clearInterval(interval);
        // Add minimal delay for UX polish
        setTimeout(() => {
          setIsDone(true);
        }, 500);
      }
    }, 70);

    return () => clearInterval(interval);
  }, []);

  return (
    <AnimatePresence onExitComplete={onComplete}>
      {!isDone && (
        <motion.div
          key="preloader"
          initial={{ opacity: 1 }}
          exit={{ 
            opacity: 0,
            y: -100,
            filter: 'blur(10px)',
            transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
          }}
          className="fixed inset-0 z-[99999] flex flex-col items-center justify-center bg-[#07080c] select-none pointer-events-auto"
        >
          {/* Subtle noise pattern matching main design context */}
          <div className="noise-overlay opacity-20" />

          {/* Core visual container */}
          <div className="w-full max-w-sm px-6 flex flex-col items-center text-center relative z-10">
            {/* Logo Visual Area */}
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="mb-8 flex flex-col items-center gap-3"
            >
              {/* Logo icon/image fallback matching Navbar */}
              {!logoImgError ? (
                <img
                  src="/assets/logo.png"
                  alt="MBJ Logo"
                  onError={() => setLogoImgError(true)}
                  className="h-10 max-h-[40px] w-auto object-contain transition-all duration-300 pointer-events-none select-none"
                />
              ) : (
                <div className="flex items-center gap-2">
                  <span className="font-sans text-3xl font-extrabold tracking-tight text-emerald-500 animate-pulse drop-shadow-[0_0_8px_rgba(16,185,129,0.3)]">MBJ</span>
                </div>
              )}
            </motion.div>

            {/* Glowing neon load percentage track */}
            <div className="w-full h-[3px] bg-neutral-800/60 rounded-full overflow-hidden relative shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)] mb-3">
              <motion.div
                className="h-full bg-[#10b981] shadow-[0_0_12px_#10b981,0_0_4px_#10b981] rounded-full"
                style={{ width: `${progress}%` }}
                transition={{ ease: 'easeOut', duration: 0.1 }}
              />
            </div>

            {/* Dynamic percentage display in mono styling */}
            <div className="flex items-center justify-between w-full px-1">
              <span className="font-mono text-[9px] font-medium text-neutral-500 tracking-wider">
                INITIALIZING CORE
              </span>
              <span className="font-mono text-[10px] font-semibold text-emerald-500 tracking-widest">
                {progress}%
              </span>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
